/**
 * Bubble scale — colour-coded circles, each able to hang its own help text beneath it as a drop.
 *
 * Split from PainScaleInput rather than built as a mode of it. The two draw the same primitive
 * and diverge on everything else: a pain scale is a fixed row of small circles holding digits,
 * while a bubble scale sizes each circle to the words inside it, picks one font size for the
 * whole row so the points read as peers, and can caption any choice rather than only the ends.
 *
 * `displayOption: "bubble"` selects this; `"pain_scale"` keeps the numeric renderer. The names
 * describe what each draws, which is the lesson `pain_scale` taught by outliving its first survey.
 *
 * This is design 1h ("Bubble") from the Scale Help Text Ideation set, followed exactly. Every
 * number in DESIGN is the drawing's own, so the two stay diffable if it is revised. The single
 * addition the drawing does not carry is the pop on select, which the rest of the station has.
 */
import React, { useState, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import QuestionTitle from "./Title";
import Help from "./Help";
import useUniformFontSize from "../../../hooks/useUniformFontSize";
import { getEnglishTranslation } from "../../../utils/englishI18n";

const channels = hex => [0, 2, 4].map(i => parseInt(hex.slice(1).substr(i, 2), 16));

const toHex = channel =>
    Math.round(channel)
        .toString(16)
        .padStart(2, "0");

const getLuminance = hex => {
    const [r, g, b] = channels(hex).map(v => {
        const channel = v / 255;
        return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Whichever of the two inks reads better on this fill — not whichever clears a threshold first.
 *
 * Taking white as soon as it passed 4.5:1 handed washed-out white text to mid-tone fills like
 * #FFA726, where the dark ink is comfortably the stronger of the two. Derived rather than seeded,
 * so a survey author choosing a colour never has to also choose a text colour.
 */
const INK = "#37474f";

// WCAG contrast is (lighter + 0.05) / (darker + 0.05), so the denominator is the ink's luminance
// PLUS the offset — 0.0573 + 0.05. Dividing by the raw luminance overstates the dark ink and
// hands dark text to fills that should carry white.
const INK_CONTRAST_DENOMINATOR = 0.1073;

const getTextColor = hex => {
    const luminance = getLuminance(hex);

    return 1.05 / (luminance + 0.05) >= (luminance + 0.05) / INK_CONTRAST_DENOMINATOR ? "#ffffff" : INK;
};

const mixWhite = (hex, amount) =>
    "#" +
    channels(hex)
        .map(v => toHex(v + (255 - v) * amount))
        .join("");

const mixBlack = (hex, amount) =>
    "#" +
    channels(hex)
        .map(v => toHex(v * (1 - amount)))
        .join("");

const NEUTRAL = "#e0e0e0";

// A choice with no colour falls back to neutral rather than to a position on some default ramp: a
// partially coloured scale keeps the colours it does declare.
const colorOf = choice => choice.color || NEUTRAL;

/**
 * Bubble size and the font range that fits inside it, from the question's existing `choice.size`.
 *
 * `maxDiameter` is a ceiling, not a size: a bubble grows to fill its share of the row and stops
 * there, so ten points on a narrow screen shrink while five on a wide one do not balloon. The
 * font range then has to cover whatever diameter that lands on — hence a range per size rather
 * than a single number. Unknown values fall to `small`.
 *
 * `comfortableDiameter` is the smallest bubble a row accepts inside the page column. Below it the
 * size's font floor no longer holds the labels, so the row leaves the column instead (see
 * TrackWrap). Four `large` bubbles clear it in the 1020px column; six do not.
 */
const SCALE_SIZES = {
    small: { maxDiameter: 72, comfortableDiameter: 72, minFontSize: 13, maxFontSize: 20 },
    medium: { maxDiameter: 200, comfortableDiameter: 160, minFontSize: 18, maxFontSize: 40 },
    large: { maxDiameter: 300, comfortableDiameter: 220, minFontSize: 22, maxFontSize: 52 }
};

const scaleSize = size => SCALE_SIZES[size] || SCALE_SIZES.small;

// Vertical breathing room above and below the row.
const ROW_PADDING_PX = 12;

// A bubble never takes more than this much of the screen height, so a short viewport (Mini) keeps
// room for the question above it and the navigation below. On a full-height Maxi it never bites.
const VIEWPORT_CAP_VH = 38;

const ITEM_SIDE_PADDING_PX = 10;

// How close a row that outgrows the page column may come to the edge of the screen.
const SCREEN_GUTTER_PX = 40;

const WRAPPER_SIDE_PADDING_PX = 20;

/**
 * Whether a scale's bubbles stay in the page column or spread wider than it.
 *
 * They spread when sharing the column would leave each below its comfortable diameter. A row that
 * is still cramped once spread (six `large` bubbles on a Maxi's 1280px screen) stays spread, the
 * widest it can be, and its long words hyphenate onto a second line rather than the layout
 * changing shape: wrapped into two rows, six bubbles and their captions outgrew the screen height.
 *
 * `columnWidth` is the Wrapper's measured border box, 0 before the first measurement — which
 * reads as fitting, so jsdom, with no layout at all, renders the column as it always has.
 *
 * @returns {"column" | "spread"}
 */
export const rowLayout = ({ columnWidth, count, size }) => {
    const { comfortableDiameter } = scaleSize(size);
    const bubbleInColumn = (columnWidth - 2 * WRAPPER_SIDE_PADDING_PX) / count - 2 * ITEM_SIDE_PADDING_PX;

    return columnWidth <= 0 || bubbleInColumn >= comfortableDiameter ? "column" : "spread";
};

// A capital opening a word: the first letter of the label, or of anything after a space. Built
// from a string because this Babel setup cannot parse a `\p{…}` literal; every browser and Node
// the station runs on supports the escape itself.
const WORD_CAPITAL = new RegExp("(^|\\s)(\\p{Lu})", "gu");

/**
 * A label split so that each capitalised word's first letter stands apart from the rest.
 *
 * Chromium's English hyphenation skips any word that starts with a capital, taking it for a
 * name — so "Perimenopausal" overran its bubble while German labels, whose rules have no such
 * exception, broke cleanly. Set in its own element, the capital no longer belongs to the word the
 * engine examines, and "erimenopausal" hyphenates like any other word. The text itself is
 * unchanged, and scripts without case (Arabic, Urdu) are never split, so letters that join to
 * their neighbours stay joined.
 *
 * @param {string} text
 * @returns {Array<{text: string, initial?: boolean}>}
 */
export const labelParts = text => {
    const parts = [];
    let from = 0;

    for (const match of text.matchAll(WORD_CAPITAL)) {
        const at = match.index + match[1].length;
        if (at > from) parts.push({ text: text.slice(from, at) });
        parts.push({ text: match[2], initial: true });
        from = at + match[2].length;
    }

    if (from < text.length) parts.push({ text: text.slice(from) });
    return parts;
};

/**
 * Digits keep the monospace face the numeric scale was designed with; words do not, because a
 * monospaced sentence at kiosk size reads as a terminal readout rather than an answer.
 *
 * Derived rather than declared, which is safe here in a way a length heuristic would not be:
 * these scales store their numerals as literal labels ("1".."10") rather than i18n keys, so a
 * scale that is numeric in English is numeric in every locale.
 */
const isNumericScale = choices => choices.every(choice => /^\d+$/.test(String(choice.label)));

/**
 * Design 1h, in the 250-unit box it was drawn in. Every value is scaled by the blob's measured
 * width at render, so the shape holds at any diameter.
 */
const DESIGN_BOX = 250;
const DESIGN = {
    // The circle is smaller than the box, so the tint shows as a halo around it. That halo is
    // what makes the bubble and its caption read as one object rather than a disc on a card.
    circleInset: 21,
    circleDiameter: 208,
    // Rings of box-shadow spread on the circle — the same device the pain scale uses, at the
    // radii this drawing calls for. A shadow ring on a circle is even by construction, which a
    // stroke traced round the blob's silhouette could never be: a stroke straddles its path, so
    // it thins wherever the shape meets the edge of its own box.
    ringWhite: 10,
    ringSelected: 18,
    neckStartX: 237,
    neckStartY: 180,
    // Inside both ends of the curve, which is what bends the neck concave instead of tapering it.
    neckControlX: 205,
    neckControlY: 222,
    panelLeft: 38,
    panelRight: 212,
    panelTop: 268,
    panelCornerRadius: 36,
    captionTop: 276,
    captionInset: 46,
    captionFontSize: 24,
    captionBottomPad: 30
};

const TINT_TOWARDS_WHITE = 0.58;
const TINT_TOWARDS_WHITE_SELECTED = 0.34;
const RING_TOWARDS_BLACK = 0.18;
const CAPTION_LINE_HEIGHT = 1.3;
const MIN_CAPTION_FONT_PX = 14;

// A selected caption thickens. Named because the panel is measured at the selected weight
// whatever weight the caption currently wears, and a measurement taken at the other weight
// would size the panel for text that is not there.
const CAPTION_WEIGHT = 400;
const CAPTION_WEIGHT_SELECTED = 600;

/**
 * Joins translated strings into one value comparable with ===.
 *
 * A translate function is handed down fresh by its provider whenever the i18n instance settles,
 * so a memo listing `t` rebuilds on renders where not a word changed. Keying on the joined text
 * instead holds for as long as the copy does. NUL because no label contains one.
 */
const TEXT_SEPARATOR = "\u0000";

/**
 * The pop, and the one thing here the drawing does not carry — deliberately kept, because it is
 * behaviour rather than decoration.
 *
 * The station answers every tap with it, so a bubble scale that stayed still would be the odd one
 * out — and with `advanceOnSelect` the screen moves on after a short dwell, making this the only
 * confirmation the participant gets that the machine saw them. The spring overshoots slightly,
 * exactly as the pain scale's does.
 *
 * Far short of the pain scale's 1.2, because the ring here is much wider in proportion: the circle
 * plus its selected ring already reaches 122 of the box's 125 units, so past about 1.025 the ring
 * starts breaking out of the drop it belongs to. 1.04 overhangs by under two units, which is
 * imperceptible, and the absolute movement still lands near the pain scale's — its circle being so
 * much smaller.
 */
const POP_SCALE = 1.04;
const POP_TRANSITION = "transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.18s ease";

// Scaling the design's units by an arbitrary width produces binary-float tails like
// 254.39999999999998. SVG renders them fine, but two decimals keeps the emitted path legible in
// devtools and stable in snapshots, and is far finer than a pixel.
const round = value => Math.round(value * 100) / 100;

/**
 * The drop — circle, concave neck and caption panel — as one continuous path.
 *
 * It has to be one path rather than a circle sitting on a panel: two shapes would show the seam
 * where they meet. A `height` of 0 is a choice with nothing to say, and closes as a bare circle.
 *
 * @param {number} width  blob box width in px
 * @param {number} height blob box height in px, or 0 for an uncaptioned choice
 * @returns {string}
 */
const blobPath = (width, height) => {
    const u = width / DESIGN_BOX;
    const r = round(width / 2);

    // Two half-sweeps, not one full turn: an arc of exactly 360 degrees has no direction to
    // sweep in and renders as nothing at all.
    if (!height) {
        return `M${r},0 A${r},${r} 0 1 1 ${round(r - 0.01)},0 Z`;
    }

    const left = round(DESIGN.panelLeft * u);
    const right = round(DESIGN.panelRight * u);
    const corner = round(DESIGN.panelCornerRadius * u);
    const panelTop = round(DESIGN.panelTop * u);
    const bottom = round(height);
    const neckX = round(DESIGN.neckStartX * u);
    const neckY = round(DESIGN.neckStartY * u);
    const controlX = round(DESIGN.neckControlX * u);
    const controlY = round(DESIGN.neckControlY * u);

    return (
        `M${r},0 ` +
        `A${r},${r} 0 0 1 ${neckX},${neckY} ` +
        `Q${controlX},${controlY} ${right},${panelTop} ` +
        `L${right},${round(bottom - corner)} Q${right},${bottom} ${round(right - corner)},${bottom} ` +
        `L${round(left + corner)},${bottom} Q${left},${bottom} ${left},${round(bottom - corner)} ` +
        `L${left},${panelTop} ` +
        `Q${round(width - controlX)},${controlY} ${round(width - neckX)},${neckY} ` +
        `A${r},${r} 0 0 1 ${r},0 Z`
    );
};

/**
 * The selected and resting rings, as the drawing specifies them.
 *
 * @param {string} color the choice's own colour
 * @param {boolean} selected
 * @param {number} unit one design unit in px
 * @returns {string}
 */
const ringShadow = (color, selected, unit) => {
    const white = `0 0 0 ${round(DESIGN.ringWhite * unit)}px #fff`;
    const drop = `0 ${round(3 * unit)}px ${round(10 * unit)}px rgba(0, 0, 0, ${selected ? 0.1 : 0.14})`;

    if (!selected) {
        return `${white}, ${drop}`;
    }

    return `${white}, 0 0 0 ${round(DESIGN.ringSelected * unit)}px ${mixBlack(color, RING_TOWARDS_BLACK)}, ${drop}`;
};

/**
 * The blob's rendered width, which the path needs in pixels and CSS alone cannot report.
 *
 * Measured rather than computed from the viewport, because the width is the result of a CSS
 * `min()` of three terms — the column, the size cap and a share of screen height — and only the
 * browser knows which of them won.
 *
 * `measure` is exposed so the caller can run it from a layout effect, which is what keeps the
 * fallback width from ever reaching the screen: a ResizeObserver reports AFTER paint, so on its
 * own it would let one frame render at a width CSS never agreed to.
 *
 * @returns {[number, Function, Function]} measured width, ref to attach, and a manual re-measure
 */
const useMeasuredWidth = () => {
    const [width, setWidth] = useState(0);
    const node = useRef(null);
    const observer = useRef(null);
    const lastMeasured = useRef(null);

    const measure = useCallback(() => {
        if (!node.current) {
            return;
        }

        const measured = Math.round(node.current.getBoundingClientRect().width);

        // Compared against a ref rather than bailing out inside the updater. Returning the
        // current value from a state updater does not reliably skip the render — React may
        // still render once more before bailing — and that pass re-runs the layout effects
        // below. Not calling setWidth is the only version that costs nothing.
        if (measured === lastMeasured.current) {
            return;
        }

        lastMeasured.current = measured;
        setWidth(measured);
    }, []);

    const ref = useCallback(
        element => {
            if (observer.current) {
                observer.current.disconnect();
                observer.current = null;
            }

            node.current = element;

            if (!element || typeof ResizeObserver === "undefined") {
                return;
            }

            observer.current = new ResizeObserver(() => measure());
            observer.current.observe(element);
        },
        [measure]
    );

    return [width, ref, measure];
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0 ${WRAPPER_SIDE_PADDING_PX}px;
    box-sizing: border-box;
`;

/**
 * The row stays in the page column unless its bubbles would be cramped there, and then spreads
 * to full size.
 *
 * The station lays every screen out in one centred column (1020px on a 1920px screen). Four
 * `large` bubbles share it at about 225px and sit well under the question and above the
 * navigation. Six get about 140px, the font search bottoms out, and long labels clip. A cramped
 * row therefore takes every bubble at its full size, stopped a gutter short of the screen edge.
 *
 * A switch rather than a sliding width. Grown only as far as a "just big enough" diameter, six
 * bubbles still clipped "Perimenopausal", because the text box is well under the bubble (78% of a
 * circle that is itself 208/250 of the box). And any rule that grows smoothly with the bubbles
 * also pulled four of them out of a column they already fitted. Never narrower than the column
 * either way; the Wrapper's `align-items: center` makes the overhang even on both sides.
 */
const TrackWrap = styled.div`
    position: relative;
    width: ${props =>
        props.spread
            ? `max(100%, min(calc(100vw - ${2 * SCREEN_GUTTER_PX}px), ${props.count *
                  (props.maxDiameter + 2 * ITEM_SIDE_PADDING_PX)}px))`
            : "100%"};
    flex-shrink: 0;
    padding: ${ROW_PADDING_PX}px 0;
`;

/**
 * The track spans bubble centre to bubble centre, not edge to edge.
 *
 * Each item owns an equal share of the width and centres its bubble in it, so the first and last
 * centres sit half a column in from each end — hence the symmetric inset.
 *
 * `top` is given in pixels from the measured bubble width rather than as a percentage of the row.
 * The row is only as tall as one bubble when nothing is captioned; once a drop hangs below, a
 * percentage would put the line somewhere in the caption. Half the bubble's own width is its
 * centre whatever else the column gained underneath.
 */
const Track = styled.div`
    position: absolute;
    left: calc(50% / ${props => props.count});
    right: calc(50% / ${props => props.count});
    top: ${props => props.centreOffset}px;
    transform: translateY(-50%);
    height: 6px;
    border-radius: 99px;
    background-image: linear-gradient(to right, ${props => props.gradient});
    opacity: 0.3;
`;

const Items = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    align-items: flex-start;
    width: 100%;
`;

// Equal-width columns rather than space-between: a bubble that grows needs a column to grow
// within, and equal columns keep every bubble the same distance from its neighbours. The side
// padding is the gap, so two full-width blobs never touch.
const Item = styled.button`
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 ${ITEM_SIDE_PADDING_PX}px;
    background: none;
    border: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    outline: none;
`;

// The box the whole drop occupies. Its width is CSS's decision — the min() of column, cap and
// viewport share — and its height follows from that width plus however tall the caption set.
const Blob = styled.span`
    position: relative;
    display: block;
    width: min(100%, ${props => props.maxDiameter}px, ${VIEWPORT_CAP_VH}vh);
    height: ${props => props.height}px;
`;

/**
 * No `preserveAspectRatio="none"`: it stretches the viewBox to fill the box on each axis
 * independently, so the circle becomes an ellipse the moment the measured and laid-out widths
 * disagree by a pixel. The default uniform fit is what keeps the shape a shape.
 */
const BlobShape = styled.svg`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
`;

/**
 * A 19% / 11% inset is what makes text-inside-a-circle work.
 *
 * Text has to clear the curve, so it is bounded by a rectangle inscribed in the bubble rather
 * than by the bubble itself: at 78% x 62% of the diameter, the corner sits at
 * sqrt(0.39^2 + 0.31^2) = 0.498 of the diameter, just inside the 0.5 radius. It also gives the
 * font search something to fail against — an unbounded box grows with its own text, so overflow
 * could never be detected and the type would never shrink.
 *
 * Every one of these values is a pixel figure derived from the measured width, and none may
 * become a percentage. CSS resolves a percentage `height` and `top` against the parent's HEIGHT,
 * and the blob is taller than it is wide whenever a caption hangs off it — so a percentage circle
 * stretches into an egg on exactly the choices that carry help text.
 */
const Bubble = styled.span`
    position: absolute;
    left: ${props => props.inset}px;
    top: ${props => props.inset}px;
    width: ${props => props.diameter}px;
    height: ${props => props.diameter}px;
    box-sizing: border-box;
    padding: ${props => props.padY}px ${props => props.padX}px;
    border-radius: 50%;
    display: flex;
    align-items: stretch;
    background-color: ${props => props.bgColor};
    color: ${props => props.textColor};
    box-shadow: ${props => props.shadow};
    transition: ${POP_TRANSITION};
    user-select: none;
    ${props => props.selected && `transform: scale(${POP_SCALE});`}
`;

const BubbleText = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.15;
    font-family: ${props => props.fontFamily};
    font-size: ${props => props.fontSize}px;
    font-weight: 700;
    /*
     * A word longer than the circle is wide cannot wrap, so at the smallest font the size allows it
     * overruns sideways and the hidden overflow crops both ends: "Perimenopausal" as "imenopau".
     * Hyphenation follows the document's lang, which the station sets on every language change, and
     * the limit keeps words under ten letters whole, so only the long ones ever break.
     *
     * It needs the browser's rules for the language (Chromium has them for English, French,
     * German, Italian, Spanish, Dutch, Polish and more), and for English the label's capitals set
     * apart — see labelParts. No
     * overflow-wrap fallback, deliberately — breaking a word anywhere removes the sideways overflow
     * the font search shrinks against, so the type would stay large and the words get chopped.
     */
    hyphens: auto;
    hyphenate-limit-chars: 10 4 4;
`;

// Sits over the panel the path drew for it. Absolute rather than in flow because the panel's
// height is derived from this element's own height, and a box cannot size the box it depends on.
const Caption = styled.span`
    position: absolute;
    left: ${(DESIGN.captionInset / DESIGN_BOX) * 100}%;
    right: ${(DESIGN.captionInset / DESIGN_BOX) * 100}%;
    top: ${props => props.top}px;
    font-family: "Proxima Nova Alt", Helvetica, Arial, sans-serif;
    font-size: ${props => props.fontSize}px;
    line-height: ${CAPTION_LINE_HEIGHT};
    text-align: center;
    color: ${INK};
    font-weight: ${props => (props.selected ? CAPTION_WEIGHT_SELECTED : CAPTION_WEIGHT)};
    overflow-wrap: break-word;
`;

/**
 * What the drawing's numbers become at a given bubble width.
 *
 * One object rather than ten loose values: it is the half of a bubble's appearance that is the
 * same for every point on the scale, so computing it once per resize and handing it down as a
 * single stable prop is both less to read and less for the memo below to compare.
 */
const geometryFor = (blobWidth, maxDiameter, fontSize, numeric) => {
    const unit = blobWidth / DESIGN_BOX;
    const circleDiameter = round(DESIGN.circleDiameter * unit);

    return {
        unit,
        blobWidth,
        maxDiameter,
        fontSize,
        circleInset: round(DESIGN.circleInset * unit),
        circleDiameter,
        // The inscribed rectangle is a fraction of the CIRCLE, so it is measured from the circle.
        circlePadY: round(circleDiameter * 0.19),
        circlePadX: round(circleDiameter * 0.11),
        captionTop: round(DESIGN.captionTop * unit),
        captionFontSize: Math.max(MIN_CAPTION_FONT_PX, Math.round(DESIGN.captionFontSize * unit)),
        fontFamily: numeric ? '"DM Mono", monospace, sans-serif' : '"Proxima Nova Alt", Helvetica, Arial, sans-serif'
    };
};

/**
 * One point on the scale.
 *
 * Memoised because a tap changes two bubbles — the one gaining the selection and the one losing
 * it — while the row renders as a whole, and a styled component re-derives its CSS every time it
 * is rendered. Without this, a ten-point scale rebuilds eight bubbles to look exactly as they
 * already did.
 *
 * `bubble` and `geometry` are objects the parent holds still, and `selected` picks between the
 * two fills and two rings the bubble already carries, so the shallow compare is four values.
 */
const BubbleItem = React.memo(({ bubble, geometry, selected, onSelect }) => (
    <Item type={"button"} onClick={() => onSelect(bubble.value)} aria-label={bubble.label} aria-pressed={selected}>
        <Blob
            data-testid={"bubble-box"}
            ref={bubble.boxRef}
            maxDiameter={geometry.maxDiameter}
            height={bubble.blobHeight}
        >
            <BlobShape viewBox={`0 0 ${geometry.blobWidth} ${bubble.blobHeight}`}>
                <path
                    data-testid={"bubble-blob"}
                    d={bubble.path}
                    fill={selected ? bubble.selectedFill : bubble.restFill}
                />
            </BlobShape>
            <Bubble
                data-testid={"bubble-circle"}
                inset={geometry.circleInset}
                diameter={geometry.circleDiameter}
                padY={geometry.circlePadY}
                padX={geometry.circlePadX}
                bgColor={bubble.color}
                textColor={bubble.textColor}
                shadow={selected ? bubble.selectedShadow : bubble.restShadow}
                selected={selected}
            >
                <BubbleText
                    data-testid={"bubble-text"}
                    ref={bubble.textRef}
                    fontFamily={geometry.fontFamily}
                    fontSize={geometry.fontSize}
                >
                    {/* One wrapper, because BubbleText centres with flex and every child node of a
                        flex box is an item of its own: unwrapped, a capital and the rest of its
                        word would sit side by side as two boxes. */}
                    <span>
                        {labelParts(bubble.label).map((part, index) =>
                            part.initial ? <span key={index}>{part.text}</span> : part.text
                        )}
                    </span>
                </BubbleText>
            </Bubble>
            {bubble.caption && (
                <Caption
                    data-testid={"bubble-caption"}
                    ref={bubble.captionRef}
                    top={geometry.captionTop}
                    fontSize={geometry.captionFontSize}
                    selected={selected}
                >
                    {bubble.caption}
                </Caption>
            )}
        </Blob>
    </Item>
));

BubbleItem.displayName = "BubbleItem";

BubbleItem.propTypes = {
    bubble: PropTypes.object.isRequired,
    geometry: PropTypes.object.isRequired,
    selected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired
};

const BubbleInput = ({ model, selected, onSelect, t, i18n }) => {
    const [localSelected, setLocalSelected] = useState(selected || null);
    const [captionHeights, setCaptionHeights] = useState({});

    const choices = model.getChoices();
    const { maxDiameter, minFontSize, maxFontSize } = scaleSize(model.getChoiceSize());

    const [measuredWidth, widthRef, measureWidth] = useMeasuredWidth();
    const [columnWidth, columnRef, measureColumn] = useMeasuredWidth();
    const spread =
        rowLayout({ columnWidth, count: Math.max(choices.length, 1), size: model.getChoiceSize() }) === "spread";
    const captionHeightsRef = useRef({});
    const captionNodes = useRef([]);

    // Falling back to the cap rather than to zero matters twice: it is the right optimistic guess
    // for the very first paint, and it is the only width jsdom will ever offer, since nothing
    // there lays anything out.
    const blobWidth = measuredWidth || maxDiameter;

    // One size for every bubble on the scale, not one per bubble: sized individually, "None" would
    // render large next to a small "Moderate difficulty" and the points would stop reading as peers.
    //
    // Translated on every render — there is no way to know the words are unchanged without asking
    // — but joined, so everything downstream can settle while the copy holds still.
    const labelText = choices.map(choice => t(choice.label, choice.label)).join(TEXT_SEPARATOR);
    const captionText = choices.map(choice => (choice.helpText ? t(choice.helpText) : "")).join(TEXT_SEPARATOR);

    // Re-measured when the bubble's width settles. The first pass runs against the fallback, and
    // the type has to be refitted to the width CSS actually chose while both are still ahead of
    // the paint — otherwise the correction lands a frame late and is seen: a bubble scale that
    // painted its label at the designed maximum and visibly shrank it on the next frame.
    //
    // And when the words change: translations arriving after the first render, or a language
    // switch mid-question, change the copy in boxes that stay exactly the same size, so nothing
    // resizes and a refit keyed on width alone would never run.
    const { fontSize, registerCard } = useUniformFontSize(choices.length, {
        minFontSize,
        maxFontSize,
        remeasureOn: `${blobWidth}${TEXT_SEPARATOR}${labelText}`
    });

    const geometry = useMemo(() => geometryFor(blobWidth, maxDiameter, fontSize, isNumericScale(choices)), [
        blobWidth,
        maxDiameter,
        fontSize,
        choices
    ]);

    /**
     * Everything that differs from one point to the next, worked out once per scale.
     *
     * A single pass rather than a memo per quantity. The colour arithmetic alone is five
     * derivations a bubble — and `getTextColor` raises three channels to a power — none of which
     * can change when a choice is picked: picking swaps which of the two fills and which of the
     * two rings is used, it does not change what they are. The ref callbacks ride along here so
     * they are stable too, which is what lets the memo on BubbleItem actually hold.
     */
    const bubbles = useMemo(() => {
        const labels = labelText.split(TEXT_SEPARATOR);
        const captions = captionText.split(TEXT_SEPARATOR);

        // Two lines' worth, used only until a caption has been measured. Guessing short would
        // make the panel visibly grow on the second paint; two lines covers most captions.
        const estimate = Math.round(geometry.captionFontSize * CAPTION_LINE_HEIGHT * 2);

        return choices.map((choice, index) => {
            const color = colorOf(choice);
            const height = choice.helpText
                ? Math.round(
                      geometry.captionTop +
                          (captionHeights[index] || estimate) +
                          DESIGN.captionBottomPad * geometry.unit
                  )
                : 0;

            return {
                // The stored answer is the English label, as for a standard choice button:
                // scoring and reporting match on that text, never on the i18n key.
                value: getEnglishTranslation(choice.label, choice.label, i18n),
                label: labels[index],
                caption: choice.helpText ? captions[index] : "",
                color,
                textColor: getTextColor(color),
                restFill: mixWhite(color, TINT_TOWARDS_WHITE),
                selectedFill: mixWhite(color, TINT_TOWARDS_WHITE_SELECTED),
                restShadow: ringShadow(color, false, geometry.unit),
                selectedShadow: ringShadow(color, true, geometry.unit),
                path: blobPath(geometry.blobWidth, height),
                blobHeight: height || geometry.blobWidth,
                // Only the first blob is measured; the rest have nothing to attach.
                boxRef: index === 0 ? widthRef : undefined,
                textRef: registerCard(index),
                captionRef: node => {
                    captionNodes.current[index] = node;
                }
            };
        });
    }, [choices, labelText, captionText, captionHeights, geometry, widthRef, registerCard, i18n]);

    const gradient = useMemo(() => {
        const colors = bubbles.map(bubble => bubble.color);

        // linear-gradient needs two stops to be valid CSS, so a degenerate one-point scale
        // repeats its single colour rather than emitting a rule the browser throws away.
        return (colors.length > 1 ? colors : [...colors, ...colors]).join(", ");
    }, [bubbles]);

    // Before paint, so the very first frame is drawn at the width CSS actually chose rather than
    // at the fallback: a ResizeObserver reports after paint and would let one frame through.
    //
    // The column first, since whether the row spreads decides the bubble's width; `spread` in the
    // dependencies re-measures the bubble in the same pre-paint pass once the row has changed.
    useLayoutEffect(() => {
        measureColumn();
        measureWidth();
    }, [measureColumn, measureWidth, maxDiameter, choices.length, spread]);

    /**
     * Each caption's own height drives its panel.
     *
     * The dependencies are the point. Every read here is a forced synchronous layout taken right
     * after styled-components has written fresh rules, so an ungated effect pays that on every
     * render — including the several a participant causes just by walking the row. Nothing in
     * this list changes when a choice is picked.
     */
    useLayoutEffect(() => {
        const next = { ...captionHeightsRef.current };
        let changed = false;

        captionNodes.current.forEach((node, index) => {
            if (!node) {
                return;
            }

            // Measured bold whatever weight it is wearing. Bold is the taller of the two states,
            // so one reading sizes the panel for both and the drop cannot change height mid-tap
            // — which is what selecting would otherwise do to a caption that gains a line.
            const worn = node.style.fontWeight;
            node.style.fontWeight = String(CAPTION_WEIGHT_SELECTED);
            const height = Math.round(node.getBoundingClientRect().height);
            node.style.fontWeight = worn;

            if (next[index] !== height) {
                next[index] = height;
                changed = true;
            }
        });

        if (changed) {
            captionHeightsRef.current = next;
            setCaptionHeights(next);
        }
    }, [blobWidth, captionText, choices.length]);

    // Held still so the memoised items are not handed a fresh callback every render, which would
    // defeat the point of memoising them. `onSelect` is a bound method on the question group.
    const handleSelect = useCallback(
        value => {
            setLocalSelected(value);
            onSelect(value);
        },
        [onSelect]
    );

    return (
        <Wrapper ref={columnRef}>
            <QuestionTitle htmlFor={"bubble-scale"}>{t(model.getDisplayText())}</QuestionTitle>
            {model.getHelpText() && <Help>{t(model.getHelpText())}</Help>}
            <TrackWrap count={Math.max(choices.length, 1)} maxDiameter={maxDiameter} spread={spread}>
                <Track
                    data-testid={"bubble-track"}
                    gradient={gradient}
                    count={Math.max(choices.length, 1)}
                    centreOffset={ROW_PADDING_PX + blobWidth / 2}
                />
                <Items>
                    {bubbles.map((bubble, index) => (
                        <BubbleItem
                            key={index}
                            bubble={bubble}
                            geometry={geometry}
                            selected={localSelected === bubble.value}
                            onSelect={handleSelect}
                        />
                    ))}
                </Items>
            </TrackWrap>
        </Wrapper>
    );
};

BubbleInput.propTypes = {
    model: PropTypes.object.isRequired,
    selected: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    i18n: PropTypes.object
};

export default withTranslation()(BubbleInput);
